import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ContractsService, Contract } from '../../services/contracts.service';
import { environment } from '../../environments/environmentCAS';
import { ApiMapperUtil } from '../../utils/api-mapper.util';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-product-catalog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    DialogModule,
    ConfirmDialogModule,
    ToastModule,
    TagModule,
    TooltipModule
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './product-catalog.component.html',
  styleUrl: './product-catalog.component.css'
})
export class ProductCatalogComponent implements OnInit {
  contracts: Contract[] = [];
  loading = false;
  private togglingProductKeys = new Set<string>();
  allProducts: ProductRow[] = [];
  filteredProducts: ProductRow[] = [];
  insurers: string[] = [];

  searchTerm = '';
  selectedInsurer: string | null = null;
  selectedStatus: 'all' | 'active' | 'inactive' = 'all';
  selectedTarget: string | null = null;

  targetOptions = [
    { label: 'Toutes les cibles', value: null },
    { label: 'TNS', value: 'TNS' },
    { label: 'Tout le monde', value: 'Tout le monde' },
    { label: 'Senior', value: 'Senior' },
    { label: 'Actifs (35/55 ans)', value: 'Actifs_35_55' },
    { label: 'Actifs', value: 'Actifs' },
    { label: 'Retraités', value: 'Retraites' },
    { label: '35 à 65 ans', value: '35_65' },
    { label: '50 à 80 ans', value: '50_80' },
    { label: 'Moins de 75 ans', value: 'Moins_75' },
    { label: 'Senior + TNS', value: 'Senior_TNS' },
    { label: 'Expatrié', value: 'Expatrie' },
    { label: 'Frontalier', value: 'Frontalier' },
    { label: 'Jeune', value: 'Jeune' },
    { label: 'Particuliers', value: 'Particuliers' }
  ];

  private normalizeForSearch(text: string, isAssureur = false): string {
    return this.contractsService.normalizeText(text, isAssureur);
  }



  showDialog = false;
  dialogMode: 'create' | 'edit' = 'create';
  currentContract: Contract = {};

  pdfFile: File | null = null;
  extractLevelName = '';
  extracting = false;

  logoUploading = false;

  constructor(
    private contractsService: ContractsService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.loadContracts();
  }

  loadContracts(): void {
    this.loading = true;
    this.contractsService.getAllAdminContracts().subscribe({
      next: (contracts) => {
        this.contracts = contracts;
        this.rebuildCatalog();
        this.loading = false;
      },
      error: (error) => {
        console.error('Erreur lors du chargement des contrats:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Impossible de charger les contrats'
        });
        this.loading = false;
      }
    });
  }

  // Vue "produits" : regrouper les niveaux par (assureur + nom de contrat) une seule fois
  private buildProducts(): ProductRow[] {
    const groups = new Map<string, Contract[]>();

    this.contracts.forEach(c => {
      const normalizedInsurer = this.normalizeForSearch(c.insurer || '', true);
      const normalizedName = this.normalizeForSearch(c.contract_name || '');
      const key = `${normalizedInsurer}||${normalizedName}`;
      
      const existing = groups.get(key);
      if (existing) {
        existing.push(c);
      } else {
        groups.set(key, [c]);
      }
    });

    const products: ProductRow[] = [];
    groups.forEach(levels => {
      const first = levels[0];
      const anyActive = levels.some(l => l.active !== false);

      let displayName = first.contract_name;
      if (first.insurer === 'Alptis' && first.contract_name === 'Alptis') {
        displayName = 'Luminéis';
      }

      products.push({
        insurer: first.insurer,
        contract_name: displayName,
        contract_type: first.contract_type,
        logo: first.logo,
        levels,
        active: anyActive
      });
    });

    return products;
  }

  rebuildCatalog(): void {
    this.insurers = this.buildInsurers();
    this.allProducts = this.buildProducts();
    this.applyFilters();
  }

  onFiltersChanged(): void {
    this.applyFilters();
  }

  private applyFilters(): void {
    const term = this.normalizeForSearch(this.searchTerm);
    this.filteredProducts = this.allProducts.filter(product => {
      const matchesSearch = this.searchTerm
        ? (this.normalizeForSearch(product.contract_name || '').includes(term) ||
           this.normalizeForSearch(product.contract_type || '').includes(term) ||
           this.normalizeForSearch(product.insurer || '', true).includes(term))
        : true;

      const matchesInsurer = this.selectedInsurer
        ? this.normalizeForSearch(product.insurer || '', true) === this.normalizeForSearch(this.selectedInsurer, true)
        : true;

      const anyActive = product.levels.some(l => l.active !== false);
      const allInactive = product.levels.every(l => l.active === false);

      const matchesStatus = this.selectedStatus === 'all'
        ? true
        : this.selectedStatus === 'active'
          ? anyActive
          : allInactive;

      let matchesTarget = true;
      if (this.selectedTarget) {
        matchesTarget = ApiMapperUtil.isProductMatchingTarget(
          product.contract_name || '', 
          product.insurer || '', 
          this.selectedTarget
        );
      }

      return matchesSearch && matchesInsurer && matchesStatus && matchesTarget;
    });
  }

  private buildInsurers(): string[] {
    const insurerMap = new Map<string, string>();
    this.contracts.forEach(c => {
      if (c.insurer) {
        const normalized = this.normalizeForSearch(c.insurer, true);
        if (!insurerMap.has(normalized)) {
          insurerMap.set(normalized, c.insurer);
        } else {
          // Si une version en majuscule arrive ensuite, on la préfère pour l'affichage propre
          const current = insurerMap.get(normalized)!;
          if (c.insurer.toUpperCase() === c.insurer && current.toUpperCase() !== current) {
            insurerMap.set(normalized, c.insurer);
          }
        }
      }
    });
    return Array.from(insurerMap.values()).sort();
  }

  openCreateDialog(): void {
    this.dialogMode = 'create';
    this.currentContract = {
      insurer: '',
      contract_name: '',
      contract_type: '',
      level_id: '',
      level_name: '',
      active: true
    };
    this.showDialog = true;
  }

  editContract(contract: Contract): void {
    this.dialogMode = 'edit';
    this.currentContract = { ...contract };
    this.showDialog = true;
  }

  hideDialog(): void {
    this.showDialog = false;
    this.currentContract = {};
    this.pdfFile = null;
    this.extractLevelName = '';
    this.extracting = false;
  }

  onPdfFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.pdfFile = input.files[0];
    } else {
      this.pdfFile = null;
    }
  }

  onLogoFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];
    this.logoUploading = true;

    this.contractsService.uploadLogo(file).subscribe({
      next: (res) => {
        this.currentContract.logo = `${environment.flaskUrl}${res.url}`;
        this.logoUploading = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Logo ajouté',
          detail: 'Le logo a été attaché au produit.'
        });
      },
      error: (error) => {
        console.error('Erreur lors de l\'upload du logo:', error);
        this.logoUploading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Impossible d\'ajouter le logo.'
        });
      }
    });
  }

  extractFromPdf(): void {
    if (!this.pdfFile || !this.extractLevelName) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation',
        detail: 'Veuillez sélectionner un PDF et renseigner le niveau de contrat.'
      });
      return;
    }

    this.extracting = true;
    this.contractsService.extractContractFromPdf(this.pdfFile, this.extractLevelName).subscribe({
      next: (contract) => {
        // Fusionner les données extraites dans le contrat courant
        this.currentContract = {
          ...this.currentContract,
          ...contract,
          active: this.currentContract.active ?? true
        };

        this.messageService.add({
          severity: 'success',
          summary: 'Extraction réussie',
          detail: 'Les données ont été pré-remplies depuis le PDF.'
        });
        this.extracting = false;
      },
      error: (error) => {
        console.error('Erreur lors de l\'extraction depuis le PDF:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Erreur lors de l\'extraction depuis le PDF.'
        });
        this.extracting = false;
      }
    });
  }

  saveContract(): void {
    if (!this.currentContract.level_id) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation',
        detail: 'level_id est obligatoire'
      });
      return;
    }

    if (this.dialogMode === 'create') {
      this.contractsService.createAdminContract(this.currentContract).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: 'Produit créé avec succès'
          });
          this.hideDialog();
          this.loadContracts();
        },
        error: (error) => {
          console.error('Erreur lors de la création du produit:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: 'Erreur lors de la création du produit'
          });
        }
      });
    } else {
      this.contractsService.updateAdminContract(this.currentContract.level_id!, this.currentContract).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: 'Produit mis à jour avec succès'
          });
          this.hideDialog();
          this.loadContracts();
        },
        error: (error) => {
          console.error('Erreur lors de la mise à jour du produit:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: 'Erreur lors de la mise à jour du produit'
          });
        }
      });
    }
  }

  // (Dés)activation d'un produit complet : tous les niveaux du même contrat
  toggleActive(product: any): void {
    console.log('toggleActive clicked', product);
    if (!product?.levels || product.levels.length === 0) {
      return;
    }
    const key = this.getProductKey(product);
    if (this.togglingProductKeys.has(key)) {
      return;
    }
    this.togglingProductKeys.add(key);

    const anyActive = product.levels.some((l: Contract) => l.active !== false);
    const makeActive = !anyActive;

    const insurer = ((product.insurer as string) || '').trim();
    const contractName = ((product.contract_name as string) || '').trim();

    const request$ = makeActive
      ? this.contractsService.activateProduct(insurer, contractName)
      : this.contractsService.deactivateProduct(insurer, contractName);

    request$.pipe(
      finalize(() => this.togglingProductKeys.delete(key))
    ).subscribe({
      next: () => {
        this.messageService.add({
          severity: makeActive ? 'success' : 'error',
          summary: makeActive ? 'Activation' : 'Désactivation',
          detail: makeActive
            ? 'Produit activé (tous les niveaux)'
            : 'Produit désactivé (tous les niveaux)'
        });
        product.levels.forEach((l: Contract) => {
          l.active = makeActive;
        });
        product.active = makeActive;
        this.applyFilters();
      },
      error: (error) => {
        console.error('Erreur lors du changement de statut du produit:', error);
        this.messageService.add({
          severity: 'error',
          detail: 'Erreur lors du changement de statut'
        });
        this.loadContracts();
      }
    });
  }

  private getProductKey(product: any): string {
    return `${(product?.insurer || '').toString().trim()}||${(product?.contract_name || '').toString().trim()}`;
  }

  isTogglePending(product: any): boolean {
    return this.togglingProductKeys.has(this.getProductKey(product));
  }

  // Suppression d'un produit complet : tous les niveaux
  deleteProduct(product: any): void {
    console.log('deleteProduct clicked', product);
    if (!product?.levels || product.levels.length === 0) {
      return;
    }

    this.confirmationService.confirm({
      message: `Supprimer définitivement tous les niveaux du produit ${product.contract_name} ?`,
      header: 'Confirmer la suppression',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        const requests = product.levels
          .filter((l: Contract) => !!l.level_id)
          .map((l: Contract) => this.contractsService.deleteAdminContract(l.level_id as string));

        if (requests.length === 0) {
          return;
        }

        forkJoin(requests).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Succès',
              detail: 'Produit supprimé (tous les niveaux)'
            });
            this.loadContracts();
          },
          error: (error) => {
            console.error('Erreur lors de la suppression du produit:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Erreur',
              detail: 'Erreur lors de la suppression du produit'
            });
            this.loadContracts();
          }
        });
      }
    });
  }

  deleteContract(contract: Contract): void {
    if (!contract.level_id) {
      return;
    }

    this.confirmationService.confirm({
      message: `Supprimer définitivement le produit ${contract.contract_name} (${contract.level_name}) ?`,
      header: 'Confirmer la suppression',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.contractsService.deleteAdminContract(contract.level_id!).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Succès',
              detail: 'Produit supprimé'
            });
            this.loadContracts();
          },
          error: (error) => {
            console.error('Erreur lors de la suppression du produit:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Erreur',
              detail: 'Erreur lors de la suppression du produit'
            });
          }
        });
      }
    });
  }

  getStatusSeverity(active?: boolean): string {
    if (active === true) return 'success';
    if (active === false) return 'danger';
    return 'info';
  }

  getStatusLabel(active?: boolean): string {
    if (active === true) return 'Actif';
    if (active === false) return 'Inactif';
    return 'Inconnu';
  }

  getInsurerLogo(contract: Contract): string | null {
    // Priorité au logo spécifique du contrat (URL)
    if (contract.logo) {
      return contract.logo;
    }

    const insurerRaw = contract.insurer || '';
    if (!insurerRaw) return null;

    // Normaliser le nom (minuscule, sans accents, sans caractères spéciaux)
    const normalized = insurerRaw
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]/g, '');

    const map: { [key: string]: string } = {
      'aesio': 'assets/images/logos/AESIO.PNG',
      'alptis': 'assets/images/logos/ALPTIS.PNG',
      'apivia': 'assets/images/logos/APIVIA.png',
      'april': 'assets/images/logos/APRIL.PNG',
      'asaf': 'assets/images/logos/ASAF.PNG',
      'axa': 'assets/images/logos/AXA.PNG',
      'entoria': 'assets/images/logos/ENTORIA.PNG',
      'generali': 'assets/images/logos/GENERALI.PNG',
      'harmonie': 'assets/images/logos/HARMONIE.PNG',
      'henner': 'assets/images/logos/HENNER.PNG',
      'malakoff': 'assets/images/logos/MALAKOFF.PNG',
      'neoliane': 'assets/images/logos/NEOLIANE.PNG',
      'sollyazar': 'assets/images/logos/SOLLYAZAR.PNG',
      'spvie': 'assets/images/logos/SPIVE.PNG',
      'swisslife': 'assets/images/logos/SWISSLIFE.PNG',
      'utwin': 'assets/images/logos/UTWIN.PNG',
      // Zenioo : on accepte zenio / zenioo
      'zenio': 'assets/images/logos/ZENIO.PNG',
      'zenioo': 'assets/images/logos/ZENIO.PNG',
      '2ma': 'assets/images/logos/2MA.PNG',
      // Nouveaux logos
      'acheel': 'assets/images/logos/ACHEEL.PNG',
      'mutualia': 'assets/images/logos/MUTUALIA.PNG',
      'seyna': 'assets/images/logos/SEYNA.PNG'
    };

    // Faire un matching par inclusion sur le nom normalisé
    for (const key of Object.keys(map)) {
      if (normalized.includes(key)) {
        return map[key];
      }
    }

    return null;
  }
}

interface ProductRow {
  insurer?: string;
  contract_name?: string;
  contract_type?: string;
  logo?: string;
  levels: Contract[];
  active: boolean;
}
